import React, { useMemo, useCallback } from 'react';
import { dashboardTokens as t } from './tokens';
import { toTitleCase } from '../../utils/toTitleCase';
import { withLanguage, format } from '../common/Texts';
import JacquesChartBase from '../JacquesChartBase';

// Same visual system as ReportJacquesChart; renders employee dots instead of a
// candidate trace, reusing the shared chart chrome and projection.
const WIDTH = 940;
const HEIGHT = 620;
const MAX_WIDTH = 1120;

const DOT_R = 5.5;
// Radio de captura del tooltip, en unidades del viewBox. Replica el
// comportamiento del canvas legacy: al pasar por un punto se listan todas las
// personas que caen encima o inmediatamente al lado, no sólo la que quedó
// dibujada arriba.
const HOVER_R = 12;
// Tope de nombres listados; el resto se resume como "+N más" para que un cúmulo
// grande no genere un tooltip más alto que el propio gráfico.
const MAX_TIP_NAMES = 12;

export default function DashboardJacquesChart({ dots = [], companyChart, language }) {
    return (
        <JacquesChartBase
            width={WIDTH}
            height={HEIGHT}
            maxWidth={MAX_WIDTH}
            companyChart={companyChart}
            language={language}
        >
            {({ project, showTip, hideTip }) => (
                <EmployeeDots
                    dots={dots}
                    project={project}
                    showTip={showTip}
                    hideTip={hideTip}
                    language={language}
                />
            )}
        </JacquesChartBase>
    );
}

// Mitad izquierda o derecha del punto, para los cúmulos que mezclan categorías.
function halfCircle(cx, cy, r, side) {
    const sweep = side === 'right' ? 1 : 0;
    return `M ${cx} ${cy - r} A ${r} ${r} 0 0 ${sweep} ${cx} ${cy + r} Z`;
}

function EmployeeDots({ dots, project, showTip, hideTip, language }) {
    const tr = useMemo(() => withLanguage(language), [language]);

    // Es habitual que varias personas caigan en la misma coordenada exacta (misma
    // edad proyectada, mismo sublevel y mismo rango etario). Dibujamos un único
    // elemento por coordenada — sin desplazarlo ni agrandarlo — pero coloreado
    // según las categorías que conviven en él; `points` conserva a todas las
    // personas para el tooltip.
    const { marks, points } = useMemo(() => {
        const all = dots
            .filter((d) => d.age != null && d.level != null)
            .map((d) => ({ ...project(d), altopot: d.altopot, tip: d.tip }));

        const byCoord = new Map();
        for (const p of all) {
            const label = toTitleCase(p.tip || '');
            const key = `${p.x.toFixed(2)},${p.y.toFixed(2)}`;
            const mark = byCoord.get(key);
            if (mark) {
                mark.hipo = mark.hipo || p.altopot;
                mark.standard = mark.standard || !p.altopot;
                mark.hasLabel = mark.hasLabel || !!label;
            } else {
                byCoord.set(key, {
                    x: p.x,
                    y: p.y,
                    hipo: p.altopot,
                    standard: !p.altopot,
                    hasLabel: !!label,
                });
            }
        }
        return { marks: [...byCoord.values()], points: all };
    }, [dots, project]);

    const handleHover = useCallback((mark) => (event) => {
        const names = [];
        let extra = 0;
        for (const p of points) {
            const dx = p.x - mark.x;
            const dy = p.y - mark.y;
            if (dx * dx + dy * dy > HOVER_R * HOVER_R) continue;
            const label = toTitleCase(p.tip || '');
            if (!label) continue;
            if (names.length < MAX_TIP_NAMES) names.push(label);
            else extra += 1;
        }
        if (!names.length) return;
        if (extra) names.push(format(tr('and_n_more'), extra));
        showTip(event, names);
    }, [points, showTip, tr]);

    return marks.map((m, i) => {
        const handlers = {
            onMouseEnter: handleHover(m),
            onMouseMove: handleHover(m),
            onMouseLeave: hideTip,
            style: { cursor: m.hasLabel ? 'pointer' : 'default' },
        };

        if (!(m.hipo && m.standard)) {
            return (
                <circle
                    key={i}
                    cx={m.x}
                    cy={m.y}
                    r={DOT_R}
                    fill={m.hipo ? t.color.hipo : t.color.standard}
                    fillOpacity={0.85}
                    stroke="#fff"
                    strokeWidth={1.5}
                    {...handlers}
                />
            );
        }

        // Cúmulo mixto: media luna por categoría, con el mismo borde y tamaño
        // que un punto simple.
        return (
            <g key={i} {...handlers}>
                <path
                    d={halfCircle(m.x, m.y, DOT_R, 'left')}
                    fill={t.color.standard}
                    fillOpacity={0.85}
                />
                <path
                    d={halfCircle(m.x, m.y, DOT_R, 'right')}
                    fill={t.color.hipo}
                    fillOpacity={0.85}
                />
                <circle cx={m.x} cy={m.y} r={DOT_R} fill="none" stroke="#fff" strokeWidth={1.5} />
            </g>
        );
    });
}
